import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { MapPin, Users, AlertCircle, Shield, Heart } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Cell } from "recharts";

const HEAT_COLORS = ["#DBEAFE", "#93C5FD", "#60A5FA", "#2563EB", "#1E3A8A", "#082F6B"];
const CHART_MARGIN = { left: 10, right: 20 };
const CHART_RADIUS = [0, 4, 4, 0];
const Y_TICK_STYLE = { fontSize: 12 };

function heatColor(count, max) {
  if (!max) return HEAT_COLORS[0];
  const pct = count / max;
  if (pct > 0.8) return HEAT_COLORS[5];
  if (pct > 0.6) return HEAT_COLORS[4];
  if (pct > 0.4) return HEAT_COLORS[3];
  if (pct > 0.2) return HEAT_COLORS[2];
  if (pct > 0.05) return HEAT_COLORS[1];
  return HEAT_COLORS[0];
}

function fmtMoney(v) {
  return `R$ ${Number(v || 0).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`;
}

function heatLegendLabel(index, total) {
  if (index === 0) return "muito baixa";
  if (index === total - 1) return "muito alta";
  return "";
}

export default function MapaBairrosPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/mapa-bairros")
      .then((r) => setData(r.data))
      .catch((e) => logger.error("Falha ao carregar mapa", e))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-slate-500">Carregando...</div>;
  if (!data) return <div className="p-8 text-slate-500">Falha ao carregar dados</div>;

  const items = data.items || [];
  const maxCount = Math.max(1, ...items.map((i) => i.total));
  const topBairros = items.slice(0, 15);

  return (
    <div className="space-y-6" data-testid="mapa-bairros-page">
      <div>
        <h1 className="text-3xl font-black text-[#083CA6] flex items-center gap-3">
          <MapPin className="w-8 h-8" /> Mapa de Inscritos por Bairro
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Análise territorial · <strong>{data.total_bairros}</strong> bairros · <strong>{data.total_inscricoes.toLocaleString("pt-BR")}</strong> inscrições totais
        </p>
      </div>

      {/* Gráfico de barras — Top 15 bairros */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top 15 bairros com mais inscrições</CardTitle>
          <CardDescription>Barras coloridas por densidade (mais escuro = mais concentração)</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={Math.max(280, topBairros.length * 32)}>
            <BarChart data={topBairros} layout="vertical" margin={CHART_MARGIN}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="bairro" width={140} tick={Y_TICK_STYLE} />
              <Tooltip
                formatter={(v, _n, p) => [
                  `${v} inscrições · ${p.payload.aprovadas} aprovadas · ${p.payload.pendentes} pendentes`,
                  "Total",
                ]}
              />
              <Bar dataKey="total" radius={CHART_RADIUS}>
                {topBairros.map((row) => (
                  <Cell key={row.bairro} fill={heatColor(row.total, maxCount)} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Grid de cards por bairro com heatmap-style */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Densidade por Bairro</CardTitle>
          <CardDescription>Cores indicam concentração de inscrições. Clique em um bairro para análise detalhada.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="bairros-grid">
            {items.map((b) => {
              const pct = Math.round((b.total / maxCount) * 100);
              const cor = heatColor(b.total, maxCount);
              const temVulnerabilidades = b.com_pcd + b.com_idoso + b.area_de_risco > 0;
              return (
                <div
                  key={b.bairro}
                  className="rounded border p-3 hover:shadow-md transition-shadow"
                  style={{ borderLeft: `6px solid ${cor}` }}
                  data-testid={`bairro-card-${b.bairro}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <div className="font-bold text-sm truncate">{b.bairro}</div>
                      <div className="text-xs text-slate-500">
                        {b.total.toLocaleString("pt-BR")} inscrições
                      </div>
                    </div>
                    <Badge
                      className="text-white text-[11px]"
                      style={{ background: cor }}
                    >
                      {pct}%
                    </Badge>
                  </div>
                  <Progress value={pct} className="h-1.5 mb-3" />

                  <div className="grid grid-cols-3 gap-1 text-[11px] mb-2">
                    <div className="bg-emerald-50 border border-emerald-100 rounded p-1 text-center">
                      <div className="font-bold text-emerald-700">{b.aprovadas + b.homologadas}</div>
                      <div className="text-emerald-600">OK</div>
                    </div>
                    <div className="bg-amber-50 border border-amber-100 rounded p-1 text-center">
                      <div className="font-bold text-amber-700">{b.pendentes + b.em_analise}</div>
                      <div className="text-amber-600">Pend.</div>
                    </div>
                    <div className="bg-red-50 border border-red-100 rounded p-1 text-center">
                      <div className="font-bold text-red-700">{b.indeferidas}</div>
                      <div className="text-red-600">Indef.</div>
                    </div>
                  </div>

                  {temVulnerabilidades && (
                    <div className="flex gap-2 text-[11px] text-slate-600 flex-wrap">
                      {b.com_pcd > 0 && (
                        <span className="flex items-center gap-1" title="Com PCD">
                          <Heart className="w-3 h-3 text-purple-600" />{b.com_pcd}
                        </span>
                      )}
                      {b.com_idoso > 0 && (
                        <span className="flex items-center gap-1" title="Com Idoso">
                          <Users className="w-3 h-3 text-orange-600" />{b.com_idoso}
                        </span>
                      )}
                      {b.area_de_risco > 0 && (
                        <span className="flex items-center gap-1" title="Área de risco">
                          <AlertCircle className="w-3 h-3 text-red-600" />{b.area_de_risco}
                        </span>
                      )}
                    </div>
                  )}

                  <div className="text-[11px] text-slate-500 mt-2 pt-2 border-t">
                    Renda média: <strong>{fmtMoney(b.renda_media)}</strong>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Legenda */}
      <Card>
        <CardContent className="py-3">
          <div className="flex items-center gap-3 flex-wrap text-xs">
            <Shield className="w-4 h-4 text-slate-500" />
            <span className="text-slate-600 font-semibold">Legenda de densidade:</span>
            {HEAT_COLORS.map((c, i) => (
              <div key={c} className="flex items-center gap-1">
                <div className="w-4 h-4 rounded" style={{ background: c }} />
                <span className="text-slate-500">{heatLegendLabel(i, HEAT_COLORS.length)}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
